import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/ui/page-header';
import { optStr, optEnum, useUrlText } from '@/lib/list-search';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Key, Mail, UserX, UserCheck, MoreVertical, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { usePermissions } from '@/common/hooks/usePermissions';
import { usersService, User, PermissionData } from '@/features/users/services/users.service';
import { permissionsService } from '@/features/users/services/permissions.service';
import { teamsService } from '@/features/teams/services/teams.service';
import { UserFormDialog } from '@/features/users/components/UserFormDialog';
import { ResetPasswordDialog } from '@/features/users/components/ResetPasswordDialog';
import { SendAlertDialog } from '@/features/users/components/SendAlertDialog';
import { DeactivateUserDialog } from '@/features/users/components/DeactivateUserDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface UsersSearch {
  search?: string;
  status?: 'active' | 'inactive';
  team?: string;
}

export const validateUsersSearch = (s: Record<string, unknown>): UsersSearch => ({
  search: optStr(s.search),
  status: optEnum(s.status, ['active', 'inactive'] as const),
  team: optStr(s.team),
});

export const Route = createFileRoute('/_authenticated/users')({
  validateSearch: validateUsersSearch,
  component: UsersPage,
});

function UsersPage() {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { search: searchQuery = '', status, team } = Route.useSearch();
  const navigate = Route.useNavigate();
  const selectedTeamId = team ?? 'all';
  const activeFilter = status ?? 'all';
  const setParams = (patch: Partial<UsersSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const [searchInput, setSearchInput] = useUrlText(searchQuery || undefined, (v) => setParams({ search: v }));

  const canCreate =
    hasPermission('users.create') || hasPermission('admin.tenant') || hasPermission('admin.system');
  const canManage = hasPermission('users.manage');
  const isAdminTenant = hasPermission('admin.tenant');
  const isAdminSystem = hasPermission('admin.system');
  const canManageUsers = canManage || isAdminTenant || isAdminSystem;

  // Buscar equipes
  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const response = await teamsService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: canManageUsers,
  });

  const teams = teamsData || [];

  // Buscar usuários
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', selectedTeamId, searchQuery, activeFilter],
    queryFn: async () => {
      const params: { teamId?: string; search?: string; activeFilter?: string } = {};
      if (selectedTeamId && selectedTeamId !== 'all') params.teamId = selectedTeamId;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (activeFilter && activeFilter !== 'all') params.activeFilter = activeFilter;
      const response = await usersService.getAll(Object.keys(params).length ? params : undefined);
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: canManageUsers,
  });

  // Buscar permissões
  const { data: permissionsData } = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const response = await permissionsService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: canManageUsers,
    staleTime: 0, // Sempre buscar dados frescos
    refetchOnMount: true,
  });

  const users: User[] = usersData || [];
  const permissions: PermissionData[] = permissionsData || [];

  const [deactivateTarget, setDeactivateTarget] = useState<User | null>(null);

  // Mutations
  const activateMutation = useMutation({
    mutationFn: (id: string) => usersService.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleCreate = () => {
    setSelectedUser(null);
    setFormOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setFormOpen(true);
  };

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    setResetPasswordOpen(true);
  };

  const handleSendAlert = (user: User) => {
    setSelectedUser(user);
    setAlertOpen(true);
  };

  // O diálogo confere os equipamentos que estão com a pessoa antes de desativar
  const handleDeactivate = (user: User) => setDeactivateTarget(user);

  const handleActivate = async (user: User) => {
    try {
      await activateMutation.mutateAsync(user.id);
    } catch (error: any) {
      // o interceptor global já mostra o toast de erro
      console.error('Erro ao ativar usuário:', error);
    }
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Usuários"
        description="Gerencie usuários e permissões"
        actions={
          canCreate && (
            <Button onClick={handleCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              Novo usuário
            </Button>
          )
        }
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Lista de Usuários</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-56 sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={activeFilter}
                onValueChange={(v) => setParams({ status: v === 'all' ? undefined : (v as UsersSearch['status']) })}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Ativos</SelectItem>
                  <SelectItem value="inactive">Inativos</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={selectedTeamId}
                onValueChange={(v) => setParams({ team: v === 'all' ? undefined : v })}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Filtrar por equipe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as equipes</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : !users || users.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">Nenhum usuário encontrado</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Equipes</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Data de Criação</TableHead>
                    <TableHead>Última atividade</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.fullName || '-'}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.active ? 'default' : 'secondary'}>
                          {user.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.teams && user.teams.length > 0 ? (
                            user.teams.slice(0, 2).map((team) => (
                              <Badge key={team.id} variant="outline" className="text-xs">
                                {team.name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">Sem equipe</span>
                          )}
                          {user.teams && user.teams.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{user.teams.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.role ? (
                          <Badge variant="outline" className="text-xs">
                            {user.role.name}
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">Sem cargo</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(user.createdAt), 'dd/MM/yyyy', {
                          locale: ptBR,
                        })}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {user.lastActivity
                          ? format(new Date(user.lastActivity), "dd/MM/yyyy 'às' HH:mm", {
                              locale: ptBR,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {canManageUsers && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label={`Ações de ${user.fullName || user.email}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(user)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                                <Key className="mr-2 h-4 w-4" />
                                Redefinir Senha
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleSendAlert(user)}>
                                <Mail className="mr-2 h-4 w-4" />
                                Enviar Alerta
                              </DropdownMenuItem>
                              {user.active ? (
                                <DropdownMenuItem
                                  onClick={() => handleDeactivate(user)}
                                  className="text-destructive"
                                >
                                  <UserX className="mr-2 h-4 w-4" />
                                  Desativar
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleActivate(user)}>
                                  <UserCheck className="mr-2 h-4 w-4" />
                                  Ativar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modais */}
      <UserFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={handleSuccess}
        user={selectedUser || undefined}
        permissions={permissions}
      />

      <DeactivateUserDialog
        open={!!deactivateTarget}
        onOpenChange={(open) => !open && setDeactivateTarget(null)}
        user={deactivateTarget}
      />

      <ResetPasswordDialog
        open={resetPasswordOpen}
        onOpenChange={setResetPasswordOpen}
        user={selectedUser}
        onSuccess={handleSuccess}
      />

      <SendAlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        user={selectedUser}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
